import React, { useState } from "react";

import { useConfirmPrintJobMutation, useDiscardPrintJobMutation } from "../../../api/printingApi";
import { Btn, Icon, Stepper } from "../../catalogProductStudio/edit/StudioUi";
import { describeVariant, fill, getApiErrorDetails, plural } from "../printCenterModel";

/**
 * "Some failed": per line, how many labels came out right. The failed ones can
 * be printed again (another attempt of the same job) before confirming, or
 * the job can be confirmed for what printed — the rest goes back to Needs labels.
 */
function SomeFailedDialog({ tt, job, onClose, onPrintFailedAgain, onConfirmed, notify }) {
  const [okByLine, setOkByLine] = useState(() =>
    Object.fromEntries(job.lines.map((line) => [line.id, String(line.quantity)]))
  );
  const [confirmJob, { isLoading }] = useConfirmPrintJobMutation();
  const failedLines = job.lines
    .map((line) => ({ line, failed: Math.max(0, line.quantity - Number(okByLine[line.id] || 0)) }))
    .filter((entry) => entry.failed > 0);

  const confirmPrinted = async () => {
    try {
      const updated = await confirmJob({
        id: job.id,
        body: {
          lines: job.lines
            .filter((line) => line.newStockQuantity > 0)
            .map((line) => ({
              lineId: line.id,
              confirmedNewStock: Math.min(line.newStockQuantity, Number(okByLine[line.id] || 0)),
            })),
        },
      }).unwrap();
      onConfirmed(updated);
    } catch (error) {
      notify(getApiErrorDetails(error).message || tt("actionError"), "danger");
    }
  };

  return (
    <div className="pse-overlay pse-overlay--dialog" role="presentation">
      <button type="button" className="pse-overlay__backdrop" aria-label={tt("close")} onClick={onClose} />
      <div className="pse-dialog pc-dialog pc-dialog--wide" role="dialog" aria-modal="true" aria-labelledby="pc-failed-title">
        <h2 id="pc-failed-title" className="pse-dialog__title">{tt("someFailedTitle")}</h2>
        <p className="pc-muted pc-small">{tt("someFailedCopy")}</p>
        <ul className="pc-failed-lines">
          {job.lines.map((line) => (
            <li key={line.id}>
              <div>
                <strong>{line.snapshot.productTitle}</strong>
                <span className="pc-muted pc-small"> · {describeVariant(line.snapshot)}</span>
                {line.newStockQuantity > 0 ? (
                  <span className="pc-demand-text pc-small pc-block">{fill(tt("lineNewStockOf"), { newStock: line.newStockQuantity, total: line.quantity })}</span>
                ) : null}
              </div>
              <label className="pc-qty-field">
                <span>{tt("printedOk")}</span>
                <Stepper
                  value={okByLine[line.id]}
                  onChange={(value) => setOkByLine((current) => ({ ...current, [line.id]: value }))}
                  min={0}
                  max={line.quantity}
                  ariaLabel={tt("printedOk")}
                  decreaseLabel={tt("decrease")}
                  increaseLabel={tt("increase")}
                />
                <span className="pc-muted pc-small">{fill(tt("ofTotal"), { total: line.quantity })}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className="pse-dialog__actions">
          <Btn onClick={onClose}>{tt("cancel")}</Btn>
          <Btn
            icon="printer"
            disabled={!failedLines.length}
            onClick={() => onPrintFailedAgain(failedLines.map(({ line, failed }) => ({ line, quantity: failed })))}
          >
            {plural(tt, "printFailedAgain", failedLines.reduce((sum, entry) => sum + entry.failed, 0))}
          </Btn>
          <Btn variant="primary" disabled={isLoading} onClick={confirmPrinted}>
            {tt("confirmWhatPrinted")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/**
 * The browser cannot tell whether paper came out, so new-stock labels wait for
 * a person to say so. Manual prints and reprints never ask.
 */
function PrintConfirmationBar({ tt, job, onDone, onHide, onPrintFailedAgain, notify }) {
  const [showFailed, setShowFailed] = useState(false);
  const [confirmJob, { isLoading: isConfirming }] = useConfirmPrintJobMutation();
  const [discardJob, { isLoading: isDiscarding }] = useDiscardPrintJobMutation();
  const busy = isConfirming || isDiscarding;

  const confirmAll = async () => {
    try {
      const updated = await confirmJob({ id: job.id }).unwrap();
      notify(plural(tt, "confirmedAll", job.newStockLabels), "success");
      onDone(updated);
    } catch (error) {
      notify(getApiErrorDetails(error).message || tt("actionError"), "danger");
    }
  };

  const nothingPrinted = async () => {
    try {
      const updated = await discardJob(job.id).unwrap();
      notify(tt("discarded"), "info");
      onDone(updated);
    } catch (error) {
      notify(getApiErrorDetails(error).message || tt("actionError"), "danger");
    }
  };

  return (
    <>
      <div className="pc-confirm-bar" role="region" aria-live="polite" aria-label={tt("confirmQuestion")}>
        <Icon name="printer" size={20} />
        <div className="pc-confirm-bar__text">
          <strong>{tt("confirmQuestion")}</strong>
          <span className="pc-small">
            {fill(tt("confirmJobMeta"), { job: job.id, total: job.totalLabels, newStock: job.newStockLabels })}
          </span>
        </div>
        <div className="pc-confirm-bar__actions">
          <Btn variant="primary" icon="checkCircle" disabled={busy} onClick={confirmAll}>
            {tt("confirmAll")}
          </Btn>
          <Btn disabled={busy} onClick={() => setShowFailed(true)}>{tt("confirmSomeFailed")}</Btn>
          <Btn variant="ghost" disabled={busy} onClick={nothingPrinted}>{tt("confirmNothing")}</Btn>
          <button type="button" className="pse-icon-btn" aria-label={tt("confirmLater")} title={tt("confirmLater")} onClick={onHide}>
            <Icon name="x" size={16} />
          </button>
        </div>
      </div>
      {showFailed ? (
        <SomeFailedDialog
          tt={tt}
          job={job}
          notify={notify}
          onClose={() => setShowFailed(false)}
          onPrintFailedAgain={(lines) => {
            setShowFailed(false);
            onPrintFailedAgain(job, lines);
          }}
          onConfirmed={(updated) => {
            setShowFailed(false);
            notify(tt("confirmedPartial"), "success");
            onDone(updated);
          }}
        />
      ) : null}
    </>
  );
}

export default PrintConfirmationBar;
